function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { bandHistogramPropsSchema, barChartPropsSchema, capacityTreemapPropsSchema, chartHeaderPropsSchema, comparisonChartPropsSchema, comparisonKpiPropsSchema, comparisonTablePropsSchema, dateRangePickerPropsSchema, dayTypeSelectPropsSchema, entitySpotlightPropsSchema, equipmentCheckTablePropsSchema, gymMultiSelectPropsSchema, gymSelectPropsSchema, heatmapPropsSchema, hierarchicalFilterPropsSchema, kpiCardLivePropsSchema, kpiCardPropsSchema, metricTablePropsSchema, panelCardPropsSchema, rankedRowsPropsSchema, roomSelectPropsSchema, segmentedControlPropsSchema, suggestionsPropsSchema, timeSeriesChartPropsSchema } from "./chunk-L6FNNBQC.mjs";
// src/charts/manifest/chartManifest.ts
var panel = function(propsSchema, requiresData) {
    var runtime = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false, optionalData = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : false;
    return {
        propsSchema: propsSchema,
        requiresData: requiresData,
        runtime: runtime,
        optionalData: optionalData
    };
};
var gridPanel = function(propsSchema, requiresData) {
    var runtime = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
    return {
        propsSchema: propsSchema,
        requiresData: requiresData,
        runtime: runtime,
        optionalData: false,
        companionRequired: true
    };
};
var CHART_PANELS = {
    KPICard: panel(kpiCardPropsSchema, true, true),
    // ⭐ RUNTIME AT LAST, AND BY ITS REAL CONSUMER (row 26, the Operations dashboard). Its
    // headline is a LIVE value with no endpoint and no URL — the twin-viewer's dGraph
    // WebSocket subscription — so it could not have had a wrapper before `hostValues` existed
    // (FR4). The definition names the key it wants (`liveValue`); the host owns the map. The
    // rest of the card (subline, freshness, sparkline) binds through the normal data path, so
    // this is one component reading two channels, not a second data transport.
    KPICardLive: panel(kpiCardLivePropsSchema, true, true),
    // TimeSeriesChart runtime wrapper = the hour-of-day profile panel (equipment-overview P6).
    TimeSeriesChart: panel(timeSeriesChartPropsSchema, true, true),
    // BarChart runtime wrapper lands with row 19 (utilisation-trend): a ranked bar set off
    // `ranking`. Order and cardinality are the service's (`direction`/`n`) — the wrapper never
    // re-ranks or caps.
    BarChart: panel(barChartPropsSchema, true, true),
    BandHistogram: panel(bandHistogramPropsSchema, true, true),
    // ComparisonChart runtime wrapper lands with row 14 (equipment-comparison, Dashboard 1.2a): the
    // A-vs-B hour-of-day profile. Its `profile` mode is new in the same change — the component's `grain`
    // axis could not express hour-of-day before it, which is the mirror image of the lesson row 19 wrote
    // down (`runtime: true` does not mean the wrapper can express what you need; here the component
    // itself could not, and read as usable because it existed).
    ComparisonChart: panel(comparisonChartPropsSchema, true, true),
    // ⭐ RUNTIME AT LAST, AND BY ITS REAL CONSUMER — the `equipment-comparison` side-by-side card
    // (ledger row 14 revision, 2026-08-30). Schema-only since inception.
    //
    // ⚠️ THE NOTE THAT STOOD HERE SAID THIS WOULD STAY SCHEMA-ONLY, AND ITS REASONING WAS CORRECT
    // AND IS NOW DISCHARGED — reversed rather than deleted, because the reason is what a later
    // reader needs. It said: 1.2a's five measure rows go to `ComparisonTable` (EC-D7), leaving this
    // component's only remaining job the Embeddable ratio banner, which EC-D8 ruled out because
    // "its delta is a SUBTRACTION and a ratio is always ≥ 1, so the arrow and the tone would both
    // be wrong", with the destination named as `deltaMode: 'difference' | 'ratio'` on Rob queue
    // item 23 — and a wrapper with no consumer is what the minimal-build gate forbids.
    //
    // ALL THREE PREMISES MOVED, on Peter's rulings of 2026-08-28:
    //   - EC-D7 overturned (A10) — the Embeddable side-by-side model beats the transposed table, so
    //     this component IS the card and `ComparisonTable` loses its consumer here;
    //   - EC-D8 overturned (A11) — the ratio banner is "helpful, and part of the comparison
    //     component";
    //   - so `deltaMode` was BUILT rather than deferred, in exactly the shape queue item 23 named.
    //     The gate was never wrong: the wrapper landed the moment a consumer existed.
    //
    // ⚠️ EXTENDED IN PLACE, NOT FORKED. It is now a MULTI-measure card; `measures` of length 1 is
    // the old single-metric hero. A sibling A/B card would have been two live copies of one job
    // (ratchet rule 12). The NAME no longer describes the contract — raised for Rob at
    // ratification, backlogged, deliberately not renamed mid-build.
    ComparisonKPI: panel(comparisonKpiPropsSchema, true, true),
    // Runtime wrapper lands with row 26 (Operations "busiest areas now" / "predicted busy").
    // Written GENERAL-CASE against a `ranking`-shaped envelope, because row 16 is its second
    // consumer. ⚠️ Order and cardinality are the SERVICE's (`direction`/`n`) — the wrapper
    // never re-ranks, never slices and never caps, exactly as `BarChartPanel` does not.
    // ⚠️ No `precision` prop: Ops's two lists are 0–100 percentages and the component's
    // `Math.round` at 0 dp is correct for them. Row 16 owns `precision` (its Underused values
    // live in 0–5 h·wk⁻¹) and Ops is not a reason to pull that work forward (FR5).
    RankedRows: panel(rankedRowsPropsSchema, true, true),
    // ⭐ RUNTIME AT LAST, BOTH OF THEM, AND BY THEIR REAL CONSUMER (row 17, the `spatial-heatmap`
    // dashboard). Schema-only since inception — row 30 shipped both onto Chart.js against
    // production-SHAPE fixtures and closed with deferral D-13 precisely because nobody had seen them
    // against real customer values.
    //
    // What had to exist before a wrapper could: a way to enumerate a whole hierarchy LEVEL. Neither
    // mark is a single-group mark, and until query-service 2.9.0 (ruling SH-1) a caller had to NAME
    // its bids — which a definition may not construct or parse (NFR2), and which for at least one real
    // twin at one real catalogue level was already impossible (64 groups against a cap of 50). So this
    // is the same lesson `KPICardLive` and `ComparisonTable` wrote down: `runtime: false` was not an
    // oversight, it was the absence of the thing the wrapper would have had to bind.
    //
    // ⚠️ `Heatmap` also gained `rows` + `rowHeader` in this same release (its row axis was a WEEK),
    // and both marks gained `precision`. Those are props widenings and are SUBSUMED by the
    // availability bump rather than needing a floor of their own: no definition can bind either mark
    // below it at all (`validate.ts` hard-fails a definition naming a `runtime: false` component), so
    // the new props cannot be authored below the floor by construction. That is NOT true of
    // `SegmentedControl`'s boolean option values, which is bindable today and therefore does move the
    // floor on its own account — see the v21 entry in `manifest.ts`.
    Heatmap: gridPanel(heatmapPropsSchema, true, true),
    CapacityTreemap: gridPanel(capacityTreemapPropsSchema, true, true),
    // Runtime wrapper lands with row 26 (Operations "equipment to check"). The wrapper owns
    // the THREE-STATE composition off one envelope — site-wide banner / calibrated all-clear /
    // flagged table (FR6) — because the definition model has no conditional rendering and must
    // not acquire any: a conditional in a definition is a rules engine, and rules live in BI.
    EquipmentCheckTable: panel(equipmentCheckTablePropsSchema, true, true),
    // ⭐ RUNTIME AT LAST, AND BY ITS REAL CONSUMER. Row 15 v2 left this schema-only with the note that
    // "its real consumer is 1.2a (row 14), whose A/B contract it was written for" — this is row 14. It is
    // an A/B table (`{label, a, b, delta}` with the `comparison.a/b` series chips and a `deltaTone`),
    // used TRANSPOSED here: rows are the five MEASURES, columns the two SIDES. Its wrapper reads side A
    // on the `sideA` binding and side B on `sideB`. 1.2b's ranked multi-metric table is `MetricTable` below — two
    // components, two contracts. See `spec-replace-embeddable-manufacturer-comparison-table.md` §3 for
    // the identification, and this dashboard's spec §19.2 for what it took to act on it.
    ComparisonTable: panel(comparisonTablePropsSchema, true, true),
    // MetricTable runtime wrapper lands with row 15 v2 (manufacturer-comparison, the "Compared"
    // table): one row per entity, N AUTHORED metric columns, client-side sorting. General by
    // design — any leg emitting `{group_bid, group_label, values}` rows can bind it.
    MetricTable: panel(metricTablePropsSchema, true, true),
    // ⭐ RUNTIME AT LAST, AND BY ITS REAL CONSUMER — dashboard 1.3 (ledger row 16), the only
    // dashboard in the estate that makes a RECOMMENDATION. The note below explained why this
    // stayed schema-only after row 26, and every word of it was correct AT THE TIME: the
    // argument was that flipping it "would make it bindable as a PANEL IN ITS OWN RIGHT, which
    // nothing wants". Row 16 is what wants it. The composition point it makes about
    // `EntitySpotlight` is untouched and still applies to that component.
    Suggestions: panel(suggestionsPropsSchema, true, true),
    // ⚠️ STAYS SCHEMA-ONLY AFTER ROW 26, AND THAT IS A DECISION RATHER THAN AN OMISSION.
    // `RankedRows` and `EquipmentCheckTable` compose this component DIRECTLY — they import it
    // and hand it the host's `onActivate`/`onHover` — so composition never goes through the
    // manifest and needs no `runtime` flag; it already worked at v17. Flipping it would make it
    // bindable as a PANEL IN ITS OWN RIGHT, which nothing wants and which could not render
    // anything sensible: its only authored prop is a `bid`, and a bID is an opaque catalogue
    // key that is never displayed (row 35). A wrapper with no consumer is what the
    // minimal-build gate forbids. (The row-26 spec's floor table listed it as a fourth
    // availability user; that reading conflates composition with binding. The derived floor is
    // 18 either way — three availability users are a bump exactly as four would be.)
    EntitySpotlight: panel(entitySpotlightPropsSchema, false),
    PanelCard: panel(panelCardPropsSchema, false),
    // ChartHeader runtime wrapper lands in equipment-overview CP4 (P1 scope title). It is
    // OPTIONAL-DATA (equipment-overview-parity P5): static for P1, but P5 binds a
    // `peak_hours_view` data source for the peak-hours note (degrades gracefully if it fails).
    ChartHeader: panel(chartHeaderPropsSchema, false, true, true)
};
var CHART_CONTROLS = {
    HierarchicalFilter: panel(hierarchicalFilterPropsSchema, true, true),
    // GymSelect runtime wrapper lands with row 14 (Dashboard 1.2a's two rails; row 19's F3 gym filter is
    // its second consumer). ⚠️ `requiresData: FALSE`, changed here from `true`: its options come from the
    // HOST CONTEXT, not from a binding. No catalogue endpoint lists the caller's organisation's twins —
    // `GET /organisations/:bid/twins` exists in the middleware but is absent from the vendored
    // TL-Query-Service OpenAPI, so validator stage (c) hard-fails a binding to it, correctly. Leaving
    // `requiresData: true` would make the validator DEMAND a binding this control must not have.
    GymSelect: panel(gymSelectPropsSchema, false, true),
    // ⭐ THE ESTATE'S FIRST DATA-BOUND SINGLE SELECT (ledger row 18, the Classes consolidation), and
    // the one new capability that moves this row's manifest floor. `requiresData: TRUE` — unlike
    // `GymSelect` directly above, whose options come from the host context, this control's options come
    // from a real catalogue read (`/api/event_filters`, whose `entities` are the twin's rooms), so the
    // validator SHOULD demand its binding. `HierarchicalFilter` is the only other `requiresData: true`
    // control, and it is the leg this one was copied from.
    //
    // `runtime: true` FROM BIRTH: the wrapper ships in the same PR as this entry. A `runtime: false`
    // control is a definition-authorable component the renderer cannot draw, and `validate.ts`
    // hard-fails a definition naming one — which is the correct behaviour and the reason the two must
    // never be split across PRs.
    RoomSelect: panel(roomSelectPropsSchema, true, true),
    // THE ESTATE'S FIRST MULTI-SELECT (epic `replace-embeddable`, lane 7a), and the one new
    // capability that moves this row's manifest floor. Built once because all three multi-gym
    // dashboards need it — Manufacturer Comparison (7b), Equipment Comparison per side (7e) and
    // Equipment Utilisation (7f) — and three lanes each growing their own would have given one
    // interaction three behaviours.
    //
    // `requiresData: FALSE`, for exactly `GymSelect`'s reason directly above: the options come from
    // the HOST CONTEXT, not from a binding. No catalogue endpoint lists the caller's organisation's
    // twins, so `requiresData: true` would make the validator DEMAND a binding this control must
    // not have.
    //
    // `runtime: true` FROM BIRTH, on the `RoomSelect` precedent: a `runtime: false` control is
    // definition-authorable and undrawable, and `validate.ts` hard-fails a definition naming one.
    // The entry and its wrapper must never split across PRs.
    GymMultiSelect: panel(gymMultiSelectPropsSchema, false, true),
    DayTypeSelect: panel(dayTypeSelectPropsSchema, false, true),
    // SegmentedControl (K9, D3) — the token-native labelled pill group. runtime:true —
    // its control wrapper ships in this feature (chartPanels.tsx CHART_PANEL_COMPONENTS).
    SegmentedControl: panel(segmentedControlPropsSchema, false, true),
    // DateRangePicker runtime wrapper lands with row 19. State-owning, and it is where the
    // three-month rule (Peter, 2026-08-05) is finally IMPLEMENTED — via the new
    // `defaultWindow`/`maxSpan` props — because QS leaves daily/weekly/monthly spans
    // unbounded and the fact_usage views carry no window predicate. Other dashboards author
    // those props rather than reimplementing the rule.
    DateRangePicker: panel(dateRangePickerPropsSchema, false, true)
};
// src/components/DashboardRenderer/manifest.ts
var DASHBOARD_PANEL_MANIFEST = {
    // 🚨 THIS FLOOR WAS DERIVED AS 16 AND IS 17 BECAUSE #1206 MERGED FIRST — **RE-DERIVED, NOT RENUMBERED.**
    // Row 14 and row 15 v3 developed concurrently and each independently derived 15 → 16, for DIFFERENT
    // mechanisms: v3's is a top-level props widening (`showLabelColumn`), this one's is component
    // availability. #1206 landed first, so v16 is its version and this became the next one. The re-derivation
    // was redone against the NEW base rather than the number being incremented: on a **v16** renderer — i.e.
    // `staging` as it now stands — `ComparisonChart`, `ComparisonTable` and `GymSelect` still resolve to
    // manifest entries with no runtime wrapper, so four of six cells still degrade, and the mechanism and its
    // conclusion both survive the rebase unchanged. Had the mechanism been the props widening instead, the
    // two bumps might have COLLAPSED into one and renumbering would have hidden that question entirely.
    // §15.18's "never analogise" applies to your own earlier derivation too.
    //
    // v17: equipment-comparison (row 14 / Dashboard 1.2a) — THREE catalogue entries become BINDABLE at
    // once: `ComparisonChart` and `ComparisonTable` gain runtime wrappers, and `GymSelect` gains one AND
    // flips to `requiresData: false` (its options come from the host context, because no catalogue
    // endpoint lists the caller's organisation's twins). This is the dashboard the two comparison
    // components were written for, three versions after the first of them shipped schema-only.
    //
    // ⚠️ RE-DERIVED, NOT ANALOGISED (§15.18) — and it is v11's/v14's/v15's mechanism, **un-derivable
    // COMPONENT AVAILABILITY**, recurring for the fourth time. On a v16 renderer
    // `manifestEntry(manifest.panels, 'ComparisonChart')` DOES return an entry (the schema half shipped
    // long ago), but `resolvePanelComponent` finds no wrapper, so the cell degrades `unknown-component`.
    // Same for `ComparisonTable`, and for `GymSelect` in the controls half. **FOUR of this dashboard's six
    // cells would be holes** — both data panels and both gym pickers — leaving two scope trees driving
    // nothing. `capabilityUsers` in `validate.ts` covers `rowSizing`/`grow`, composite
    // `components`/`panelLayout` and `panels[].state`, and has no notion of component availability (the
    // gap owed to row 31 as UT-R3), so the validator CANNOT derive this floor: a definition declaring 16
    // validates clean against a v17 checkout. The author declares it; `validate.spec.ts` pins it
    // two-sidedly (17 satisfiable, 18 not) and the definition pins its own.
    //
    // That is NINE bumps across THREE mechanisms, which remains the whole argument for re-deriving every
    // time rather than copying the last one's reasoning.
    //
    // ⚠️ TWO PROPS SCHEMAS WIDEN IN THIS VERSION AND NEITHER MOVES A FLOOR OF ITS OWN — stated so nobody
    // adds an 18 "for the widening". `comparisonTablePropsSchema` gains `measures`/`labelHeader`/
    // `showLabelHeader`/`labelWidth`/`ariaLabel`/`precision`, and `comparisonChartPropsSchema` gains
    // `profile`/`xAxisLabel`. Widening is normally floor-worthy — `additionalProperties: false` makes an
    // older renderer REJECT the panel outright, which is exactly v10's and v13's mechanism — but neither
    // component has a single definition consumer today, so no already-published definition can author the
    // new props, and a v16 renderer never reaches the props check for these cells anyway: it fails one
    // step earlier, at component availability. Two mechanisms point at 16; the availability one is the
    // binding reason and the widening is subsumed by it.
    //
    // ⚠️ ALSO NOT FLOORED, and it is the newest kind of change here: `DashboardContext.twins` and
    // `PanelViewProps.context`. Both are ADDITIVE and optional — the context is a HOST-supplied object,
    // not an authored key, so no definition declares it, `additionalProperties: false` never sees it, and
    // `context.requires` is unchanged (a definition still declares only `twin_bid`/`twin_name`).
    // Deliberately NOT a `DashboardContextKey`: `fromContext` resolves binding PARAMS — values that go on
    // a wire — and a control's OPTION LIST is neither (assumption EC18). A v16 renderer simply does not
    // pass `context` to a wrapper, and the only wrapper that reads it does not exist there either.
    //
    // ⚠️ AND NOT FLOORED: the provider's `equipment_comparison` leg and its `measure` projector. A
    // `measure` VALUE has no authoring surface in the manifest and no props contract. But `@repo/ui` and
    // `@repo/front-end-services` ship in ONE app build, so a deployed app without the leg renders `no
    // route mapping for endpoint`. The right instrument for a deploy-ordering dependency is Import
    // SEQUENCING (publish only after the staging build carries the code), not the floor.
    //
    // The bump does NOT raise `equipment-overview` (7), `utilisation-trend` (13) or
    // `manufacturer-comparison` (16, at v3).
    //
    // v16: manufacturer-comparison **v3** (row 15 / MC-F3) — `metricTablePropsSchema` widens with a
    // top-level `showLabelColumn` boolean, plus `kind` and `indicator` inside its `columns` objects, for
    // Peter's strict-parity set: dimension columns that follow the split level, and the peak-hours column
    // carrying the same coloured indicator as average utilisation.
    //
    // ⚠️ RE-DERIVED, AND THE MECHANISM IS THE **TOP-LEVEL** PROP — v13's exact shape, not v15's component
    // availability. `MetricTable` already exists at v15, so nothing becomes newly bindable. What moves the
    // floor is `showLabelColumn`: `metricTablePropsSchema` is `additionalProperties: false`, and
    // `validatePanelProps` enumerates `Object.keys(value)` at the TOP LEVEL, so a v15 renderer reports
    // `unexpected prop "showLabelColumn"` and REJECTS the panel — a grey `props-invalid` card where the
    // dashboard's only data table should be.
    //
    // 🚨 AND AN EARLIER VERSION OF THIS NOTE NAMED THE WRONG KEY, WHICH IS WORTH KEEPING. It claimed the
    // floor moved because a v15 renderer would reject an authored `kind` inside `columns`. **It would
    // not.** `validatePanelProps` → `checkValue` has NO `items` handling at all (verified: zero
    // occurrences in `propsValidation.ts`), so it type-checks `columns` as an array and never looks
    // inside. The nested `additionalProperties: false` is enforced by AJV in the `@repo/dashboards`
    // CI/Import validator only. So the column keys would be silently IGNORED by an older renderer, and
    // `asMetricColumns` would drop them — the panel would render three metric columns of em dashes and no
    // indicators. That is the WORSE failure of the two (plausible, silent, wrong) and it is the real
    // reason the floor matters; it just is not the mechanism that fires. Two planes, two behaviours, and
    // conflating them put a false claim in a manifest comment.
    //
    // Demonstrated on both planes by `validate.spec.ts`'s "a manifest-15 renderer rejects the v3 props":
    // the renderer's own `validatePanelProps` against a v15-shaped schema, and AJV for the CI plane.
    // NOT observed live — a v15 renderer rejects this definition on its FLOOR first
    // (`requiresManifestVersion: 16` > 15), which blanks the whole dashboard rather than one panel, so no
    // walk against a v15 bundle can isolate a props rejection.
    //
    // Eight bumps, three mechanisms.
    //
    // v15: manufacturer-comparison **v2** (row 15 / MC-F2) — a NEW panel component, `MetricTable`,
    // becomes BINDABLE: its runtime wrapper landed with it, so its `runtime` flag is `true` from
    // birth. One row per entity, N AUTHORED metric columns, client-side sorting; it replaces v1's
    // four ranked bar cards with the Embeddable original's compact "Compared" table (Peter's
    // strict-parity ruling, 2026-08-17).
    //
    // ⚠️ RE-DERIVED, NOT ANALOGISED (§15.18). This is v11's and v14's mechanism — **un-derivable
    // COMPONENT AVAILABILITY** — and NOT v10's/v13's props widening. There is no widened schema
    // here: `metricTablePropsSchema` is a brand-new contract, so `additionalProperties: false` never
    // gets a chance to fire on an older renderer. What happens instead is one step earlier:
    // `manifestEntry(manifest.panels, 'MetricTable')` returns `undefined` on a v14 renderer, the
    // cell degrades `unknown-component`, and the dashboard renders **with a hole where its only data
    // table is** — v1's four bar cards having been removed in the same change. `capabilityUsers` in
    // `validate.ts` covers `rowSizing`/`grow`, composite `components`/`panelLayout` and
    // `panels[].state`, and has no notion of component availability (the gap owed to row 31 as
    // UT-R3), so the validator CANNOT derive this floor — a definition declaring 14 validates clean
    // against a v15 checkout. The author declares it; `validate.spec.ts` pins it two-sidedly (15
    // satisfiable, 16 not) and the definition pins its own.
    //
    // That is SEVEN bumps across THREE mechanisms, which is the whole argument for re-deriving every
    // time rather than copying the last one's reasoning.
    //
    // ⚠️ NOT floored here, deliberately: the `manufacturer_comparison` provider leg gains a sixth
    // `measure` projection (`table`). A `measure` VALUE has no authoring surface in the manifest and
    // no props contract, so it moves no floor of its own — but it DOES mean a deployed app build
    // must carry this PR's `@repo/front-end-services` as well as its `@repo/ui`, which is an Import
    // SEQUENCING obligation (publish only after the staging build carries the code), not a floor.
    //
    // ⚠️ `ComparisonTable` stays `runtime: false` and that is a decision, not an omission — it is an
    // A/B table whose consumer is dashboard 1.2a. See `chartManifest.ts`'s note on both entries.
    // ✅ DISCHARGED at v16: 1.2a landed and gave it its wrapper.
    //
    // The bump does NOT raise `equipment-overview` (7) or `utilisation-trend` (13).
    //
    // v14: manufacturer-comparison (row 15) — the `TimeSeriesChart` wrapper gains a THIRD mode,
    // selected by the bound endpoint: `manufacturer_comparison` draws an hour-of-day profile from a
    // comparison ROW LIST (one series per manufacturer / series / model) instead of from a
    // `BaselineViewData` weekday × hour grid.
    //
    // ⚠️ THIS IS v11/v12's MECHANISM, NOT v13's — the third of the three, and the reason §15.18 says
    // never to analogise from the last bump. Nothing about the PROPS is new, so a renderer that
    // predates this change ACCEPTS the definition, takes the mode-1 path, gets `undefined` back from
    // `pickBaselineGrid` over a row list, and renders an EMPTY chart. Not a grey `props-invalid` card
    // — a plausible, silent "no data" on a panel whose data arrived intact. `capabilityUsers` has no
    // notion of "binds an endpoint whose adapter branch landed at version N" (the same gap as v11 and
    // v12, owed to row 31 as UT-R3), so the validator cannot derive this floor and the author must
    // declare it. Pinned two-sidedly in `validate.spec.ts` (14 satisfiable, 15 not) and by
    // `manufacturer-comparison`'s own floor pin.
    //
    // It ALSO gates that dashboard's provider leg, since `@repo/ui` and `@repo/front-end-services`
    // ship in one app build and a deployed app without the leg would render `no route mapping for
    // endpoint`. That is a happy side effect, not the argument: the right instrument for a
    // deploy-ordering dependency is Import sequencing (publish only after the staging build carries
    // the code), and the floor is the instrument for a renderer-capability dependency.
    //
    // ⚠️ NOT floored here, deliberately, and stated so nobody adds it later "for symmetry":
    // `BarDatum.value` also widens to `number | null` in this version, so a row whose value is not
    // measurable keeps its label and paints no bar (MC-D2). `bars` is supplied by a panel wrapper
    // from bound data and is NOT an authored prop, so no definition can declare it and no older
    // renderer can reject it — a capability with no authoring surface needs no floor.
    //
    // The bump does NOT raise `equipment-overview` (7) or `utilisation-trend` (13).
    //
    // v13: utilisation-trend (row 19 F5) — `BarChart`'s props contract WIDENS by one optional
    // boolean, `valueAxisTitle`, which turns on D6's value-axis title (ratified for
    // `TimeSeriesChart` in the equipment-overview visual-parity work and never given to this mark).
    // Peter, 2026-08-14: "Busiest Equipment's measure is a percentage, but nothing on the chart
    // says so."
    //
    // ⚠️ A PROPS WIDENING IS NOT FLOOR-FREE, and this is the SECOND time that has had to be
    // learned — v10 says it in as many words for Heatmap/CapacityTreemap. `barChartPropsSchema` is
    // `additionalProperties: false`, so a renderer whose manifest predates the widening does not
    // ignore the unknown key: `validatePanelProps` reports `unexpected prop "valueAxisTitle"` and
    // the panel degrades to a grey card. **Observed live rather than reasoned about** — the first
    // walk run after authoring the prop (against a renderer bundle built before it) degraded BOTH
    // ranked panels with `data-degrade-reason="props-invalid"` and put ZERO ranking requests on the
    // wire. That is precisely what a published v12 renderer would do to a definition authoring the
    // prop, so the floor moves and `utilisation-trend` declares 13. (With the widened schema in the
    // bundle the same walk is green: 83 checks, 0 findings, 15 labels painted on both cards.)
    //
    // NOTE the asymmetry that makes this easy to get wrong: the same widening is INVISIBLE in CI,
    // because CI validates against the LOCAL manifest, which always has the new key. Only a
    // deployed older renderer sees the rejection — which is exactly why the floor, not a test, is
    // the mechanism. `capabilityUsers` cannot derive it either (no notion of "authors a prop added
    // at version N"), so it is the author's to declare, like v10, v11 and v12 before it. Pinned
    // two-sidedly in `validate.spec.ts` (13 satisfiable, 14 not).
    //
    // v12: utilisation-trend (row 19 completion) — the scope tree gains a two-projection emission
    // that an OLDER renderer does not have, and it must be floored for the SAME reason v11 was, by
    // the SAME un-machine-derivable mechanism. The `HierarchicalFilterControl` wrapper now (a)
    // seeds an empty selection to the hierarchy ROOT bIDs on mount, so a `bids: {fromControl:
    // "scope.bids"}` binding resolves to the top-level groups rather than staying unresolved, and
    // (b) publishes a `scope.leafBids` projection the ranked bar charts bind. A renderer shipped
    // before this change has neither: `scope.bids` never seeds (the trend fails closed forever) and
    // `scope.leafBids` resolves to nothing (both bar charts fail closed) — quiet, partial and wrong,
    // exactly what a floor is for. No `capabilityUsers` entry covers "binds a control field a
    // wrapper only just began emitting" (the same gap called out for v11 below and owed to row 31
    // as UT-R3), so the validator CANNOT derive this floor and a definition declaring 11 would
    // validate clean against a renderer that half-renders it. Declared two-sidedly in
    // `validate.spec.ts` (12 satisfiable, 13 not) and by `utilisation-trend`'s own floor pin.
    //
    // v11: utilisation-trend (row 19) — `BarChart` and `DateRangePicker` flip `runtime` false →
    // true, so both become bindable for the first time.
    //
    // ⚠️ A DEFINITION BINDING EITHER OF THEM MUST DECLARE 11, and that is enforced by NOTHING
    // but the author. `capabilityUsers` (validate.ts) floors `rowSizing` (6), composites (7)
    // and `panels[].state` (8) — it has no notion of "binds a component whose wrapper landed
    // at version N". The `runtime` check reads the LOCAL manifest, so CI catches it in-repo,
    // but a DEPLOYED v10 renderer meeting such a definition looks `BarChart` up, finds it
    // schema-only, and degrades that panel: quiet, partial and wrong — precisely what a floor
    // exists for, and exactly the reasoning that floored `panels[].state` at 8.
    //
    // v10: chart-ds-alignment PR-3 — widens the Heatmap and CapacityTreemap props contracts
    // (`showRampLegend`, `metricLabel`, `sizeLabel`). Unlike `controlsPanel`, this one is NOT
    // floor-free and a definition using those props must declare 10: both schemas are
    // `additionalProperties: false`, so a v9 renderer does not merely ignore the new keys — it
    // REJECTS the panel outright and draws a grey card. Nothing authors them today (both entries
    // are `runtime: false`). ⚠️ NOTE THE ASYMMETRY WITH v9, which IS floor-free: the floor is a
    // property of the CAPABILITY, never of the release, so neither number generalises to the
    // other. row 19 does not author any of these three props.
    //
    // Making that floor machine-derived is recommended to Rob (row 19 item UT-R3) and
    // reassigned to `retirement` (row 31). It is deliberately NOT done here: it changes the
    // floor mechanism for every definition, which would be a second species on top of an
    // already-additive change. Until then, an author binding a freshly-runtime component must
    // reason about this comment — which is the drift this header already warns about below, so
    // treat it as a known debt, not a design.
    //
    // Also carries `DateRangePicker`'s `defaultWindow`/`maxSpan` (a widening — every props
    // object that validated before still does) and a second mode on the `TimeSeriesChart`
    // wrapper. The latter needed no props change at all: the component's schema already had
    // `weekly` in its `grain` enum and `profile` as opt-in. `runtime: true` had meant "a
    // wrapper exists", not "the wrapper can express this" — the lesson that mis-scoped two of
    // the six Wave-4 dashboards.
    //
    // The bump does NOT raise the shipped `equipment-overview` definition, which stays at 7.
    //
    // v9: panel-layout-density (row 36) — `panelLayout.rows` groups a composite's children into
    // rows, and `controlsPanel` may name a sibling COMPONENT as well as a panel.
    //
    // BOTH ARE DELIBERATELY FLOOR-FREE, and tests pin that. An older renderer ignores `rows`
    // and lays the same children out in a flat column: today's content, every binding resolved,
    // nothing stranded — less capable, not wrong, which is the test a floor exists for. An
    // older renderer meeting a component-targeted `controlsPanel` resolves it to nothing and
    // emits no `aria-controls`, which is already the documented behaviour for an unresolvable
    // reference and is better than pointing at an absent id.
    //
    // v8: composite-panels PR-3 — a TOP-LEVEL panel may own control state (`panels[].state`)
    // and may name a manifest CONTROL. A v7 renderer looks a top-level panel up in
    // `manifest.panels` only, so such a panel degrades to `unknown-component`: the control
    // disappears and every dependent is stranded on its seed. Quiet, partial and wrong — so
    // it is floored, through the same `capabilityUsers` rule PR-1 added.
    //
    // The bump does NOT raise the shipped `equipment-overview` definition, which stays at 7:
    // nothing it authors is v8-only. `controlsPanel` is deliberately floor-free — on a v7
    // renderer it produces today's exact output minus one ARIA attribute, and a whole
    // dashboard of grey cards would be a far worse answer to a missing announcement. That
    // asymmetry is the point of flooring per CAPABILITY rather than per release.
    //
    // Also carries the corrected `DayTypeSelect` props contract — a widening, so every props
    // object that validated before still does (see `dayTypeSelectPropsSchema`).
    //
    // v18: ops-dashboard-config-migration (ledger row 26) — the Operations surface moves onto
    // the config-driven pipeline. RE-DERIVED against `origin/staging` d59ec9a3f at branch-cut,
    // not analogised from v17 and not renumbered: `manifest.ts` still declared 17 at that base,
    // and the derivation below was run from what this change makes AVAILABLE rather than from
    // "the last one was 17".
    //
    // ⚠️ It is the FIRST bump in the epic to ship AHEAD of the definition that needs it. This
    // release is the platform half of a two-PR split (C-α): the components, the chrome
    // capability and the cadence land here; the `operations` definition that binds them lands
    // separately, blocked on TL-Query-Service 2.7.0. That is harmless — the ceiling check is
    // `requiresManifestVersion <= manifestVersion` and no shipped definition binds any of this
    // — but it is a real ordering difference from every prior row, so it is written down here
    // rather than left for a reader to notice.
    //
    // TWO mechanisms fire at once, and they degrade differently:
    //
    //   MECHANISM 2 — un-derivable COMPONENT AVAILABILITY, recurring for the fifth time.
    //   `KPICardLive`, `RankedRows` and `EquipmentCheckTable` gain runtime wrappers and flip
    //   `runtime: false → true`. On a v17 renderer all three resolve to a manifest entry with
    //   no wrapper, so `resolvePanelComponent` finds nothing and each cell degrades
    //   `unknown-component` — a HOLE, not a grey card. Loud, and the same failure v17 itself
    //   produced.
    //
    //   MECHANISM 3 — validator-underivable capability, twice, and this is the dangerous half:
    //     • `layout.surface` — an older renderer does not reject the key (the layout reader
    //       picks the fields it knows by name). It renders, accepts, populates, and paints the
    //       panels inside the opaque glass card over the live scene. A plausible, silent, wrong
    //       dashboard.
    //     • a binding's `refreshMs` — an older renderer accepts it, ignores it, and serves a
    //       dashboard that populates CORRECTLY and then silently stops updating. Nothing grey,
    //       nothing missing: an operations view quietly showing an hour-old gym. The most
    //       deceptive degradation in the row, and the reason the cadence is floored at all
    //       rather than treated as a free runtime nicety.
    //
    // 🚨 BOTH ARE INVISIBLE IN CI, which validates against the LOCAL manifest — the one that
    // always has the key. `capabilityUsers` in `@repo/dashboards`' validator now derives the
    // two mechanism-3 floors (`SURFACE_MANIFEST_FLOOR`, `REFRESH_MS_MANIFEST_FLOOR`), so those
    // two are machine-checked from here on. Mechanism 2 remains underivable — `capabilityUsers`
    // has no notion of component availability (the gap owed to row 31 as UT-R3) — so a
    // definition declaring 17 while binding `RankedRows` still validates clean against this
    // checkout. `packages/ui/src/charts/manifest/chartManifest.test.ts` is the explicit pin.
    // (It named `manifest.version.test.ts` until row 16 — NO SUCH FILE HAS EVER EXISTED, so a
    // session re-deriving the floor would look for the named pin, fail to find it, and
    // conclude there was none. Corrected against `find`, not against memory.)
    //
    // ⚠️ `EntitySpotlight` deliberately does NOT flip. The spec's floor table listed it as a
    // fourth availability user "available for composition", but composition does not go through
    // the manifest: `RankedRows` and `EquipmentCheckTable` import the component directly and
    // hand it the host callbacks, which already works at v17. Flipping it would make it
    // BINDABLE AS A PANEL with no consumer and nothing sensible to render (its only authored
    // prop is a `bid`, and row 35 forbids displaying one) — a floor bump with no consumer,
    // which the minimal-build gate forbids. The derived floor is unchanged either way; three
    // availability users are a bump exactly as four would be.
    //
    // v19: ops-dashboard-config-migration (ledger row 26), the BINDING half — `context.requires:
    // ["live_scene"]`. RE-DERIVED from what the `operations` definition actually binds, against the
    // v18 base this branch is stacked on, and NOT analogised from v18 sitting one commit below.
    //
    // ONE mechanism, and it is the deceptive one — **MECHANISM 3, validator-underivable capability.**
    // A v18 renderer does not reject the member: it reads `context.requires` for the DATA keys it
    // knows and ignores the rest. So the dashboard renders, accepts, populates, and paints its four
    // panels over whatever the host happened to already be showing — the app never enters live-scene
    // mode, so there is no live scene behind the transparent chrome, the live headline has no
    // subscription feeding it, and fly-to resolves nothing because `entityKeyFor` is still returning
    // `'bID'`. Nothing is grey, nothing is a hole, and nothing is logged.
    //
    // ⚠️ WHY THIS IS A SEPARATE BUMP FROM v18 AT ALL, given both belong to one row: v18 shipped as
    // the platform half (PR C-α) and reached `staging` on its own. A renderer at exactly 18 is
    // therefore a real deployed state, not a hypothetical, and it is precisely the state that
    // degrades silently here. Collapsing the two would be renumbering rather than re-deriving.
    //
    // ⚠️ `layout.surface: 'sceneOverlay'` is NOT what moves this floor — it is already v18's, and a
    // definition using only that is correctly served by an 18 renderer. The two capabilities are
    // deliberately separate (FR7 rejected inferring the mode from the chrome), and so are their
    // floors.
    //
    // Machine-derived from here on: `LIVE_SCENE_MANIFEST_FLOOR` in `@repo/dashboards`' validator
    // gives this a `capabilityUsers` entry, as v18's two mechanism-3 capabilities have. Component
    // availability remains underivable (row 31, UT-R3).
    // v20: replace-embeddable-equipment-recommendations (ledger row 16), the dashboard 1.3
    // release. RE-DERIVED from what the `equipment-recommendations` definition actually binds,
    // against the v19 base this branch is cut from — not incremented, and not analogised from a
    // sibling row's expectation. (Rows 16, 17 and 18 all had plans on `staging` expecting 20 at
    // the same time; whichever lands second must RE-DERIVE against the new base rather than keep
    // this number. §15.18's "never analogise" applies to your own earlier derivation too.)
    //
    // TWO mechanisms, and the second is the one a reader is most likely to talk themselves out
    // of. Naming both matters because the next re-derivation reads this log for its precedent.
    //
    //   1. **MECHANISM 2 — component availability.** `Suggestions` becomes bindable
    //      (`chartManifest.ts`, `runtime: true`). Underivable by the validator, which has no
    //      notion of component availability (the gap owed to row 31 as UT-R3), so a definition
    //      declaring 19 while binding `Suggestions` still validates clean against this checkout.
    //
    //   2. **MECHANISM 1 — props widening.** `RankedRows` gains `precision`.
    //
    // ⚠️ DO NOT INHERIT ROW 17's SUBSUMPTION ARGUMENT FOR THE SECOND ONE. Row 17 reasoned that
    // its props widening needed no floor of its own because `validate.ts` refuses a
    // `runtime: false` component, so the new props were unauthorable below the floor BY
    // CONSTRUCTION. That argument is sound there and FAILS here, for a concrete reason:
    // `RankedRows` is ALREADY `runtime: true` at 19 and `operations` binds it on three live
    // panels. So a v19 renderer genuinely CAN meet a definition authoring `precision` — and
    // `rankedRowsPropsSchema` is `additionalProperties: false`, which means it does not IGNORE
    // the unknown key, it REJECTS the panel and paints a grey `props-invalid` card. That is the
    // v13 and v16 mechanism, observed live before those bumps. Mechanism 1 therefore fires
    // independently and genuinely here, and this floor rests on two mechanisms rather than one.
    //
    // TWO changes in the same release move NO floor, and the reasons are worth recording so a
    // future re-derivation does not count them:
    //
    //   · **`RankedRowVM.value` becomes `number | null`.** `rows` is DATA-supplied, not
    //     authorable — `rankedRowsPropsSchema` carries only `unit`/`maxValue`/`precision`/
    //     `inScope`/`emptyMessage` — so no definition can express it and an older renderer never
    //     sees the type. This is v14's precedent verbatim (the nullable `BarDatum.value`, "which
    //     needs no floor of its own (`bars` is not authorable)").
    //   · **`RankedRows`' `maxValue` default resolution** (omitted ⇒ scale to the served rows'
    //     own maximum). No new key, and the behaviour changes only where `maxValue` is OMITTED.
    //     Every shipped definition that binds this component — `operations`, on all three of its
    //     ranked panels — authors `maxValue: 100` explicitly, so no shipped rendering moves.
    //     ⚠️ That is a claim a TEST holds, not a comment: see `chartPanels.test.tsx`.
    //
    // Also in this release and deliberately NOT a floor: the `Suggestions` per-bucket empty state
    // (ER10). It is a behaviour change to a component that is `runtime: false` TODAY, so no
    // definition can reach it below N — this is where row 17's subsumption argument DOES apply —
    // and the copy is a component constant rather than an authored prop, so nothing new is
    // authorable either way.
    //
    // v21: replace-embeddable-spatial-heatmap (ledger row 17), the dashboard 3.1 release —
    // the estate's only surface that answers "WHERE in the gym?". RE-DERIVED against the base as it
    // actually stood on 2026-08-26, which is v20: row 16 shipped the day before this branch was cut,
    // so every plan written before that (rows 16, 17 and 18 all expected 20 at the same time) is one
    // out. The derivation does not change; only the base does.
    //
    // TWO mechanisms, and they need DIFFERENT arguments — which is the thing a re-derivation is most
    // likely to flatten:
    //
    //   • AVAILABILITY (11/14/15/17/18/20's, a seventh time). `Heatmap` and `CapacityTreemap` gain
    //     runtime wrappers, so a v20 renderer degrades BOTH of 3.1's data panels to
    //     `unknown-component` — holes, and loud. They flip as a PAIR: 3.1's two panels read the same
    //     two envelopes in opposite primary/aux roles, and either alone leaves a row of controls,
    //     which the minimal-build gate forbids registering.
    //
    //   • PROPS WIDENING (10/13/16/20's). `SegmentedControl`'s option `value` widens from `string` to
    //     `string | boolean`, so a control can drive a BOOLEAN wire param — the fix for a defect that
    //     was otherwise unreachable from the definition side (a string-valued control wiring
    //     `peak_hours_only` sends `"peak"`, the provider's strict `asFlag` drops it, and the panel
    //     paints ALL HOURS under a pill that says Peak Hours). ⚠️ This one is NOT subsumed and the
    //     reason is v20's exactly: `SegmentedControl` has been `runtime: true` since v4 and three live
    //     definitions bind it, so a v20 renderer really can meet a definition authoring
    //     `{ value: false, label: "All Hours" }` — and its schema TYPES that field, so it does not
    //     ignore the key, it rejects the control's props and paints a grey `props-invalid` card.
    //
    // ⚠️ FOUR changes in this release move NO floor, and a future re-derivation must not count them.
    // Three are genuinely SUBSUMED — `validate.ts` hard-fails any definition naming a `runtime: false`
    // component, and both marks were exactly that at v20, so their new props are unauthorable below
    // this floor BY CONSTRUCTION (the v17 precedent): `Heatmap`'s `rows` + `rowHeader`, `precision` on
    // both marks, and `CapacityTreemap`'s `rowHeader`. The fourth is not authored at all:
    // `TreemapItem.colorValue` accepting an explicit `null` is a DATA-supplied field (v14's
    // precedent). ⚠️ Do NOT generalise the subsumption to `SegmentedControl`.
    //
    // ⚠️ AND ONE THING THAT LOOKED LIKE A THIRD MECHANISM AND IS NOT. The plan expected a
    // validator-underivable "mechanism 3" floor for a control-driven boolean binding param, on the
    // reasoning that an older renderer would ACCEPT the key, ignore it and paint a plausible wrong
    // answer — the most deceptive degradation class, and the reason `refreshMs` was floored at all.
    // Measured rather than assumed: that degradation is unreachable, because the boolean can only
    // reach the binding via an authored prop the older SCHEMA rejects first. The failure is loud, so
    // the props-widening argument above carries it and no new constant is added to `validate.ts`.
    // Deriving it from `x-dashboard-endpoints` was also considered and rejected as more than a small
    // change: that block's `params` is a bare `string[]` with no type information, and typing it
    // would change the snapshot format the DEPLOYED middleware's console Import reads.
    //
    // ⭐ STILL v21 AFTER THE POST-#1264 FOLLOW-UP (2026-08-26), and the reason is the general one a
    // future re-derivation should reuse. That change added `minItems: 3, maxItems: 3` to `rampLabels`
    // on `Heatmap` and `CapacityTreemap` (Copilot's three unanswered findings on #1264: the schemas
    // said "array of string" while `asStringTriple` required exactly three, so a 2- or 4-label
    // definition validated and then silently fell back to the numeric legend), plus `maxItems` on the
    // props-schema DSL to express it.
    //
    // A floor answers "what is the minimum renderer version that handles this definition correctly?",
    // and it is derived from what a definition can EXPRESS. This is a NARROWING: it expresses nothing
    // new, it removes an expressible-but-broken authoring. Every definition valid under the new
    // schema was valid under the old one and renders identically on a renderer at 21, so no
    // definition's floor moves — and the one shape that changes outcome (wrong length) went from
    // "accepted and quietly ignored" to "refused at CI and at Import", which is strictly louder.
    // The DSL half is the `additionalProperties` precedent verbatim: it types the schema LANGUAGE,
    // not any component's props contract, and AJV already understood the keyword.
    //
    // ⭐ THE DIRECT PRECEDENT, and a strictly HARDER case than this one: commit `5e3d229bb` added
    // `minItems: 2` to `SegmentedControl`'s options — also a props-schema narrowing, also answering a
    // Copilot finding — on a component that has been `runtime: true` since v4 and is bound by three
    // SHIPPED definitions. One file changed; this log was not touched and no floor moved.
    //
    // A SECOND, INDEPENDENT REASON no floor can move here: the browser plane does not run AJV at all.
    // `DashboardRenderer/propsValidation.ts` is a hand-rolled checker that implements only `type`,
    // `enum`, `minimum` and `maximum` — `minItems`/`maxItems` are INERT on the render plane. A floor
    // names the minimum renderer that handles a definition correctly, and no renderer's behaviour
    // changes in either direction. ⚠️ Do NOT reuse this release's mechanism-1 template ("its schema
    // TYPES that field, so an older renderer REJECTS the panel and paints a grey card") for a
    // keyword `validatePanelProps` does not implement — that template holds for `type`, not for these.
    //
    // ⚠️ A NARROWING IS NOT AUTOMATICALLY FREE — this one is free because nothing ANYWHERE can trip it.
    // Both marks are unbindable below 21 (`validate.ts` hard-fails a `runtime: false` component) and 21
    // is not yet deployed, so no definition in the repo or in any customer's published set can author
    // `rampLabels` at all. The one repo definition that will — `spatial-heatmap`, on both its data
    // panels — authors exactly `["Quiet","Busy","Very busy"]`. A narrowing that rejected a definition
    // already in the registry would be a breaking change and would need its own answer.
    // v22: replace-embeddable-classes-consolidation (ledger row 18), the dashboard 1.2 release — the
    // LAST dashboard in the replace-Embeddable epic, and the one whose publication retires Embeddable.
    // RE-DERIVED against the base as it actually stood on 2026-08-26, which is v21: rows 16 and 17
    // shipped 20 and 21 respectively before this branch was cut, so this row's own plan (written when
    // three rows all expected 20) is TWO out. The derivation does not change; only the base does.
    //
    // ONE mechanism, and the whole floor rests on it:
    //
    //   • AVAILABILITY (11/14/15/17/18/20/21's, an eighth time). `RoomSelect` is a NEW catalogue
    //     entry, registered WITH its runtime wrapper (`runtime: true` from birth). It is the estate's
    //     first DATA-BOUND single select: `requiresData: true`, options from `/api/event_filters`,
    //     which is what separates it from `GymSelect` (host context, `requiresData: false`) and puts
    //     it alongside `HierarchicalFilter` as the second data-bound control. A v21 renderer finds no
    //     manifest entry for the name, degrades the cell to `unknown-component`, and paints dashboard
    //     1.2 with a hole where its room filter should be — and every data panel then reads EVERY
    //     room while no control on screen says so, which is the deceptive-degradation class rather
    //     than the loud one. The floor is what makes that unreachable: `validate.ts` hard-fails a
    //     definition naming a component with no runtime wrapper, so a v21 renderer refuses the
    //     definition outright instead.
    //
    // ⚠️ THE HIGHEST FLOOR THIS ROW *INHERITS* IS 16, and saying so matters because "the manifest is
    // 21 so I need 21" is wrong in both directions. Every other capability dashboard 1.2 binds is old:
    // `rowSizing`/`grid.grow` (6), composites + `panelLayout` (7), `panels[].state` (8),
    // `SegmentedControl`/`DateRangePicker`/`BarChart` bindable (≤11), `BarChart`'s
    // `valueAxisTitle`/`precision` (13), `MetricTable` bindable and its column props (15/16).
    // `panelLayout.rows` and `controlsPanel` remain floor-FREE by design (v9) — do not add one.
    //
    // ⚠️ TWO changes in this release move NO floor, and a future re-derivation must not count them.
    //
    //   1. THE BATCH FAN-OUT'S PER-ENDPOINT PROJECTOR (`BATCH_PROJECTORS` in
    //      `createMiddlewareDataProvider`). It is PROVIDER machinery, not renderer capability: a
    //      definition cannot express it, cannot switch it on, and cannot name it. It is v14's
    //      DATA-SUPPLIED precedent — the nullable `BarDatum.value` — one layer further out. Nothing
    //      in `capabilityUsers` could see it and nothing should.
    //
    //   2. `resolveBinding` OMITTING A `null`-VALUED PARAM. This is the cleared-optional contract that
    //      makes `RoomSelect`'s "All rooms" reach the wire as an ABSENT `entity_bid`, and it is
    //      genuinely floor-free for the v21-narrowing reason: it expresses nothing new. No shipped
    //      definition authors a literal-null binding param (all eight checked at build time) and no
    //      control has ever seeded a `null`, so no definition's rendering moves. What changed is that
    //      a shape which USED to reach the wire as the literal string `"null"` on a query string —
    //      i.e. a filter for a room of that name — is now an omitted param. That is a repair to an
    //      unreachable-and-broken behaviour, not a new capability.
    //
    //      ⚠️ It is deliberately paired with `unresolvedControlParams` NOT treating `null` as
    //      unresolved. Either half alone is a defect: omit-without-fetch hangs every panel behind a
    //      cleared filter forever, and fetch-without-omit sends the room named "null". Both halves
    //      carry the note; do not "simplify" one of them.
    // v24: dashboard-panel-named-bindings — a panel declares N data bindings, addressed by NAME,
    // and `auxData` is retired.
    //
    // RE-DERIVED AGAINST THE BASE AS IT ACTUALLY STOOD, which is v23: lane 7a's `GymMultiSelect`
    // landed after this branch was cut, so every note written earlier on this row expected 23 and is
    // one out. The derivation does not change; only the base does. (Third time in a row this has
    // happened on this epic — assume the base has moved, and check.)
    //
    // ⭐ A NEW MECHANISM, THE FOURTH: BINDING SHAPE. The first three are component-AVAILABILITY
    // (11/14/15/17/18/20/21/22), PROPS-WIDENING on an already-bindable component (16/19/21), and the
    // DATA-SUPPLIED kind that moves nothing (v14's nullable `BarDatum.value`). This is none of them:
    // no component is added, no component's props widen, and no payload changes. What widens is the
    // `data` FIELD ITSELF — it now accepts EITHER a single binding (unchanged, and what 35 of the 39
    // shipped bindings still author) OR a record of bindings keyed by name.
    //
    // WHAT A v23 RENDERER DOES WITH ONE, traced rather than assumed. It has no `namedBindings`, so
    // `useResolvedBinding` takes the record as a `DataBinding`: `endpoint` reads `undefined`,
    // `isDataPanel && !node.data` does NOT fire (a record is truthy), and the panel issues a fetch
    // with no endpoint. Both of dashboard 3.1's data panels and both of 1.2a's degrade to the
    // renderer's data-error card. LOUD rather than deceptive — nothing draws a wrong number — but
    // four panels of two shipped dashboards are holes, so the floor is what makes it unreachable:
    // `validate.ts` refuses the definition on a v23 runtime instead.
    //
    // ⚠️ UN-DERIVABLE, for the same reason availability is. `capabilityUsers` enumerates authored
    // CAPABILITIES; it has no notion of the SHAPE of the field a binding arrives in, so a definition
    // declaring 23 validates completely clean against this checkout and only a deployed v23 renderer
    // sees the holes. Pinned two-sidedly in `validate.spec.ts` (24 satisfiable, 25 not) and each
    // definition's OWN floor pinned separately in `definitions.spec.ts`.
    //
    // `required` on a binding rides this bump rather than carrying its own: it is meaningless outside
    // the record form, so no definition can author it below 24.
    //
    // ⚠️ THREE changes in this release move NO floor, and a future re-derivation must not count them.
    //
    //   1. THE PER-BINDING REFETCH DIFF. Restarting only the binding whose identity moved, instead of
    //      all of them, is renderer INTERNALS: no definition can express it, switch it on, or name it.
    //      It changes read COST, not what renders.
    //   2. THE `Array.from` FIX over `[...map]`. A defect repair — the spread evaluated to an empty
    //      array under this package's transpile target, so a control change refetched nothing.
    //   3. THE VALIDATOR'S `bindingsOf` WALKER. Validate-time only; a deployed renderer never runs it.
    //
    // v25: manufacturer-comparison-multi-gym (epic `replace-embeddable` lane 7b, ruling MG-1) — a
    // dashboard compares equipment across a PORTFOLIO of gyms rather than one site.
    //
    // RE-DERIVED AGAINST THE BASE AS IT ACTUALLY STOOD, which is v24. This lane was briefed while the
    // base was 23; #1353 moved it before the branch was cut. (Fourth time in a row on this epic —
    // assume the base has moved, and check `manifest.ts` rather than the last thing you read.)
    //
    // TWO mechanisms fire, both already in the catalogue and both UN-DERIVABLE:
    //
    //   MECHANISM A — CONTROL-STATE PROJECTION. `HierarchicalFilterControl` publishes a new field,
    //   `scope.levelCount`: how many distinct hierarchy depths the equipment selection spans. It
    //   exists for a READ BUDGET, not for a render — `/api/manufacturer_comparison` issues three BI
    //   reads per twin per LEVEL and refuses a request planning more than `MAX_COMPARISON_READS = 24`
    //   of them, so how many gyms may be compared depends on how deep the tree selection is.
    //   On a v24 renderer the field is simply not emitted: `resolveParam` returns `undefined`,
    //   `unresolvedControlParams` marks the binding's `scope_levels` missing, and BOTH of this
    //   dashboard's data panels hold in `loading` FOR EVER. Quiet and total.
    //   ⭐ EXACT PRECEDENT, and the reason this is a catalogued mechanism rather than a new one:
    //   `scope.leafBids` (see the v-note above) floored for word-for-word the same failure.
    //
    //   MECHANISM B — PROPS WIDENING on an already-bindable control (16/19/21 before it).
    //   `gymMultiSelectPropsSchema` gains `maxReads`, `readsPerTwinPerLevel` and `scopeControl` —
    //   the three inputs to that budget. A v24 renderer rejects all three under
    //   `additionalProperties: false` and falls back to the STATIC `maxSelected` of 6. That is not a
    //   grey card, which is why it is the more dangerous half: the control looks correct and lets a
    //   reader select six gyms at a three-level scope, which plans 54 reads and 400s. 🔻 And the
    //   published contract's justification for the static cap is itself wrong — it claims the cap
    //   "sits above the largest organisation in the estate today" and the largest has ELEVEN twins —
    //   so the reader who meets this is a real reader on a real organisation.
    //
    // ⚠️ NEITHER IS DERIVABLE. `capabilityUsers` enumerates authored CAPABILITIES; it has no notion of
    // which fields a control publishes into control state, nor of a props schema widening. A
    // definition declaring 24 validates completely clean against this checkout and only a DEPLOYED
    // v24 renderer sees either failure. Pinned two-sidedly in `validate.spec.ts` (25 satisfiable, 26
    // not) and `manufacturer-comparison`'s OWN floor pinned separately in `definitions.spec.ts`.
    //
    // ⚠️ TWO changes in this release move NO floor, and a future re-derivation must not count them.
    //
    //   1. THE PROVIDER READING PLURAL `twin_bids` on `manufacturer_comparison`. Provider behaviour,
    //      not a renderer capability — the same reason an authored `twin_bid` moved no floor at row
    //      40. A v24 renderer hands the same binding to the same provider and gets the same answer.
    //   2. THE COPY. A subtitle and an axis label are content.
    // 🚨 RE-DERIVED A SECOND TIME, AT PR TIME, AND THE NUMBER MOVED AGAIN — 25 → 26. Lane 7b
    // (`manufacturer-comparison-multi-gym`) independently derived 25 against the same base of 24, for
    // DIFFERENT mechanisms (control-state projection + props widening), and merged first. Two
    // capabilities cannot share one version, so this row re-derives rather than renumbering 7b's.
    // ⚠️ THAT IS FIVE CONSECUTIVE TIMES ON THIS EPIC that a floor written against a remembered base
    // was one out, and the fourth for THIS row alone (23 → 24 → 25 → 26). The mechanisms below have
    // been re-verified as still live at the new base: 7b widened a SIBLING control's schema
    // (`gymMultiSelectPropsSchema`) and added a control-state field, neither of which touches
    // `ComparisonKPI`'s availability or the two widenings recorded here.
    //
    // v26: replace-embeddable-comparison-component (ledger row 14 revision) — `ComparisonKPI` becomes
    // BINDABLE, and the equipment-comparison side-by-side card is its first real consumer.
    //
    // ⚠️ RE-DERIVED AT MERGE, AND THE NUMBER MOVED. This row was built against a base of 23 and
    // declared 24. So did `dashboard-panel-named-bindings`, independently and for a DIFFERENT
    // mechanism (binding shape). Both landed; two capabilities cannot share one version, so this one
    // re-derives to 26 against the base as it now stands. That is the FIFTH time on this epic that a
    // floor written against a remembered base was one out — assume the base has moved, and check.
    //
    // THREE mechanisms fire here, plus one inherited. ⚠️ THIS BLOCK SAID "ONE mechanism" UNTIL
    // 2026-09-01, WHEN THE REVIEW FOUND TWO MORE ALREADY FIRING. The number never depended on
    // them — availability alone forces it — but the repo's whole reason for keeping this ledger is
    // that a LATER RE-DERIVATION CHECKS THE REASONS, not the number. If availability is ever
    // discharged (say a renderer that stops degrading unknown components), someone reading only this
    // block would conclude the floor can drop, while two props widenings still hold it up.
    //
    // ⚠️ ALSO CORRECTED HERE: the bodies below said "v23" throughout. They were written against a
    // base of 23; it moved to 24 with `dashboard-panel-named-bindings`, then to 25 with lane 7b. The
    // MECHANISMS are unchanged; only the renderer version they degrade on.
    //
    //   • AVAILABILITY (11/14/15/17/18/20/21/22's, a ninth time). `ComparisonKPI` flips from
    //     schema-only to `runtime: true`. On a v25 renderer the name resolves to a manifest ENTRY but
    //     to NO WRAPPER, so the cell degrades `unknown-component` and 1.2a's side-by-side card — the
    //     dashboard's headline panel — is a hole. `capabilityUsers` has no notion of component
    //     availability, so a definition declaring 25 validates completely clean and only a deployed
    //     v25 renderer sees it. Un-derivable, unlike v24's binding shape, which IS derivable and is
    //     enforced in `validate.ts`.
    //
    //
    //   • PROPS WIDENING (10/13/16's, again). `gymSelectPropsSchema` gains a top-level `title`, and
    //     the definition authors it on BOTH gym pickers. `validatePanelProps` enumerates top-level
    //     keys under `additionalProperties: false`, so a v25 renderer reports `unexpected prop` and
    //     paints a grey card where each picker should be.
    //
    //   • PROPS WIDENING, A SECOND SITE — and this is the one nobody had written down.
    //     `comparisonChartPropsSchema` gains a top-level `metricMatrix`, authored on `by-hour-chart`.
    //     `ComparisonChart` has been `runtime: true` since v17, so unlike v17's own widenings (which
    //     were excused because neither component had a definition consumer) this one is live on a
    //     bindable component WITH a consumer. A v25 renderer greys the by-hour chart too.
    //
    //   • (INHERITED) BINDING SHAPE, from v24. The six named-binding records need the named-binding
    //     capability; a v23 renderer reads each as a single `DataBinding` with no endpoint. Does not
    //     add to the number — a floor is single-valued and 26 already covers it — but it is why a
    //     definition at 23 fails differently from one at 24.
    //
    // ⚠️ THE FULL RATIONALE FOR THE FLIP LIVES IN `chartManifest.ts`, on the entry itself: three
    // premises of the original "stays schema-only" ruling (EC-D7, EC-D8, the minimal-build gate) were
    // overturned by Peter on 2026-08-28, and `deltaMode` was built rather than deferred. Recorded
    // there rather than duplicated here.
    //
    // ⚠️ TWO changes in this release move NO floor. The `chartTheme.comparison` pair moving from
    // cyan + off-ramp grey to two CATEGORICAL stops (Rob §8.2 item 9, finding U7) is a token VALUE
    // change — a definition cannot express it and no authored field selects it. And re-binding the
    // definition from the comparison family to the BASELINE family changes which endpoint is named,
    // which stage (c) already checks; it is not a renderer capability.
    //
    // ─── v27 (2026-09-03, `fix/chart-tooltip-formatting`) ──────────────────────────────────────────
    //
    // ONE mechanism, and — for the first time on this epic — **NO DEFINITION FLOOR MOVES WITH IT.**
    // Stated that plainly because the ledger's habit is the opposite and a later reader will expect
    // the usual table of raised floors.
    //
    //   • PROPS WIDENING (10/13/16/26's, again). `timeSeriesChartPropsSchema` gains a top-level
    //     `precision` and a top-level `metricMeta`, so a definition can choose a chart's decimal
    //     places the way `MetricTable`'s columns have chosen a table's since 1.2b (Peter, 2026-09-03:
    //     "the common component should allow the definition to choose the type and precision for a
    //     given graph"). `validatePanelProps` enumerates top-level keys under
    //     `additionalProperties: false`, so a v26 renderer WOULD grey any panel that authored either.
    //
    // ⭐ NO SHIPPED DEFINITION AUTHORS EITHER PROP, WHICH IS WHY NO FLOOR MOVES — and that is a
    // deliberate choice, not an oversight. The correct precision for every panel binding these two
    // charts today is what the component and the wrapper already resolve without help:
    // `utilisation-trend`'s trend chart wants the default 1, and the three metric-SWITCHED panels
    // want a per-metric answer that `metricMeta()` in `chartPanels` supplies beside the label and
    // unit those modes already take from it. Authoring `precision: 1` on `utilisation-trend` to
    // express a value it already has would move its floor **13 → 27** and buy nothing.
    //
    // ⚠️ SO THE CAPABILITY SHIPS AHEAD OF ITS FIRST CONSUMER, and the cost of that is named: the
    // FIRST definition to author either prop pays the whole floor jump at once. For
    // `utilisation-trend` that is 13 → 27, a fourteen-version leap on a dashboard that greys out
    // ENTIRELY against an older renderer. Worth knowing before authoring a decimal place.
    //
    // ⚠️ ONE change in this release moves no floor and could be mistaken for one.
    // `equipment-comparison` gains `precision` INSIDE its existing authored `metricMeta` blob. That
    // node is an unconstrained `{ type: 'object' }` in `comparisonChartPropsSchema`, and
    // `validatePanelProps` never descends into it, so a v26 renderer accepts the definition and
    // ignores the key. Its floor stays 26. (The same openness means a mistyped `precison` there
    // validates clean — noted on the schema and in the PR's decision list.)
    //
    // ─── v28 (2026-09-03, Peter's review of `equipment-comparison`) ───────────────────────────────
    //
    // 🚨 DERIVED AS 27 AND LANDED AS 28, AND THAT IS THE RULE WORKING RATHER THAN A RENUMBER. The
    // tooltip lane above derived 27 against the same base of 26, on a DIFFERENT mechanism, and merged
    // first. Re-derived against the new base, not incremented: on a v27 renderer the mechanism below
    // is unchanged and still fires, so the conclusion survives the move. Sixth consecutive collision
    // on this epic, and the fifth for this dashboard's row alone (23 → 24 → 25 → 26 → 27 → 28).
    //
    // ONE mechanism: PROPS WIDENING (10/13/16/26/27's, again). `hierarchicalFilterPropsSchema` gains
    // `defaultRootIndex` — the POSITIONAL root seed that stops `equipment-comparison` opening with the
    // same scope on both sides and rendering two identical columns under "Roughly equal usage".
    //
    // ⛔ NOT COMPONENT AVAILABILITY. `HierarchicalFilter` has been bindable since v3, so a v27
    // renderer REACHES the props check and rejects under `additionalProperties: false` — a grey
    // `props-invalid` card where EACH of the two scope rails should be. The dashboard loses both of
    // its controls at once and the data panels sit at whatever their seeds hold.
    //
    // ⭐ AND UNLIKE v27, A FLOOR DOES MOVE WITH IT: `equipment-comparison` authors the new prop on both
    // rails, so its `requiresManifestVersion` goes 26 → 28 in the same commit. Deploy the renderer
    // BEFORE publishing the definition.
    //
    // ⚠️ ONE change in this release moves no floor and could be mistaken for one. `JsonSchemaNode`
    // gains `minimum` — the `additionalProperties` / `maxItems` precedent verbatim: it types the
    // schema LANGUAGE, not any component's props contract, and AJV already understood the keyword.
    // ⭐ `minimum` is NOT one of the five keywords `propsValidation.ts` warns are inert at runtime
    // (`items`, `minItems`, `maxItems`, `oneOf`, nested `properties`) — `checkValue` implements it —
    // which is what makes the floor above real rather than a CI-only rejection.
    //
    // ─── v29 (2026-09-08, `utilisation-trend` multi-gym — epic `replace-embeddable`, ruling MG-1 /
    //     Option C) ────────────────────────────────────────────────────────────────────────────────
    //
    // Equipment Utilisation draws ONE LINE PER GYM. The `TimeSeriesChart` wrapper's calendar-trend
    // mode learns query-service 4.0.0's twin-keyed envelope — `data[twin_bid][metric][group_bid]` —
    // and labels each series with its gym.
    //
    // ⚠️ DERIVED AGAINST A BASE OF 28, re-checked at PR time. That is the seventh derivation on this
    // epic and the base has moved under six of them; assume it has moved again and read this file
    // rather than the last number anyone wrote down.
    //
    // ONE mechanism: THE UN-DERIVABLE CAPABILITY (v11/v14/v15/v23/v25's MECHANISM 3, validator-underivable capability — this file's own
    // numbered label; there is no "mechanism A"), and it is v14's
    // almost exactly — a `TimeSeriesChart` wrapper mode that an older renderer accepts and then draws
    // EMPTY.
    //
    //   WHAT A v28 RENDERER DOES WITH THE NEW DEFINITION, traced rather than assumed. Nothing about
    //   the PROPS is new: `twin_bids` is a binding param, not a prop, so `validatePanelProps` has
    //   nothing to reject and `additionalProperties: false` never fires. The panel is accepted. Its
    //   provider then serves `timeseries` through the BATCH (a v28 bundle predates the per-call
    //   move), whose bridge overwrites the item's twin selector with the single path twin — so the
    //   read comes back for ONE gym at 200. The v28 adapter walks two levels of a THREE-level
    //   envelope, reads the twin bID as a metric name, finds no series, and the chart paints its
    //   EMPTY state. Silent and plausible, twice over: the wrong scope AND no diagnostic.
    //
    //   ⚠️ `capabilityUsers` ENUMERATES AUTHORED CAPABILITIES and has no notion of an adapter's input
    //   SHAPE, so a definition declaring 28 validates completely clean against this checkout. Only a
    //   deployed v28 renderer shows the hole. That is why the floor is hand-raised and hand-tested,
    //   exactly as `validate.ts` says component availability must be.
    //
    // ⛔ THREE THINGS IN THIS RELEASE MOVE NO FLOOR, and a future re-derivation must not count them.
    //
    //   1. `GymMultiSelect` BECOMING BOUND HERE. Its bindability is v23's bump and is already
    //      satisfied; a second dashboard authoring it adds no capability.
    //   2. THE THREE READ-BUDGET PROPS (`maxReads`, `readsPerTwinPerLevel`, `scopeControl`) and the
    //      `scope.levelCount` projection — v25's two mechanisms. **This definition authors NONE of
    //      them**, deliberately: those encode an UPSTREAM READ budget that scales with tree depth,
    //      and these two endpoints have no such budget. `/api/timeseries`'s only product cap is
    //      `MAX_TIMESERIES_READS = 36` over `metrics × twins`, and this dashboard binds ONE metric,
    //      so it binds at 36 gyms behind a control offering six. Authoring a fabricated budget would
    //      refuse legitimate selections. The static `maxSelected: 6` is a real cap and still never
    //      trims. So v25 is NOT part of this floor's derivation — recorded because the obvious
    //      reading of "another multi-gym dashboard" is that it must be.
    //   3. THE PROVIDER MOVING `timeseries` AND `ranking` OFF THE BATCH. Provider behaviour, not a
    //      renderer capability — the same reason an authored `twin_bid` moved no floor at row 40. It
    //      is what makes the CURRENT renderer correct; it is not something a definition can name.
    //      ⚠️ It is also why the v28 failure above is a DOUBLE one rather than a single: an old
    //      bundle carries the old transport as well as the old adapter.
    //
    // ⭐ AND THE COPY MOVES NO FLOOR EITHER — two bar-card subtitles naming the blend are content.
    //
    // ⛔ A REMOVAL AT 29 THAT MOVES NO FLOOR (component sweep, epic row 31, 2026-09-30). Four control
    // keys left `CHART_CONTROLS` — `GranularitySelect`, `MetricToggle`, `PeakToggle`, `SplitControls`
    // (Peter's ruling; no dashboard version in the Production or Staging registry named any of them).
    // A floor expresses a capability a definition can REQUIRE, and a removal offers none, so the
    // number stays 29. The consequence worth knowing: `manifestVersion` is NOT a snapshot of the key
    // set. A v29 validator before this date accepted `MetricToggle` (and rejected the other three as
    // schema-only); one after it rejects all four as unknown. A registry rollback to a version naming
    // one would now degrade that cell to `unknown-component` (none exists — the registries were
    // searched).
    //
    // ⭐ 30 — FRAMELESS PANELS AND `ShowWholeTwin` (ops-clear-selection, 2026-10-13). A single-component
    // panel may author `chrome: "none"` and render with no card; `validate.ts`
    // (`CHROME_MANIFEST_FLOOR`) floors the KEY at 30, because an older renderer would draw a card
    // round it and an older validator rejects the unknown property outright. `ShowWholeTwin` is a new
    // built-in (chart-free, data-free) that asks the host to clear the scene pick; like every new
    // component it carries no floor of its own — an older renderer degrades it to
    // `unknown-component` — and the Operations definition reaches 30 through `chrome` anyway.
    manifestVersion: 30,
    panels: _object_spread_props(_object_spread({}, CHART_PANELS), {
        TextPanel: {
            requiresData: false,
            runtime: true,
            propsSchema: {
                type: "object",
                properties: {
                    content: {
                        type: "string"
                    },
                    format: {
                        type: "string",
                        enum: [
                            "markdown",
                            "text"
                        ]
                    }
                },
                required: [
                    "content"
                ],
                additionalProperties: false
            }
        },
        ShowWholeTwin: {
            requiresData: false,
            runtime: true,
            propsSchema: {
                type: "object",
                properties: {
                    // The button's visible text and accessible name. Omit for "Show whole twin".
                    label: {
                        type: "string",
                        minLength: 1
                    }
                },
                required: [],
                additionalProperties: false
            }
        },
        StatPanel: {
            requiresData: true,
            runtime: true,
            propsSchema: {
                type: "object",
                properties: {
                    label: {
                        type: "string"
                    },
                    unit: {
                        type: "string"
                    },
                    // Decimal places to render the headline value to. Omit for as-is.
                    precision: {
                        type: "integer",
                        minimum: 0,
                        maximum: 6
                    }
                },
                required: [],
                additionalProperties: false
            }
        }
    }),
    // Interactive controls arrive with `chart-library` (K1–K8). K8 (a plain
    // BaseDropdown) is used directly by definitions, not registered as a named control.
    controls: _object_spread({}, CHART_CONTROLS)
};
var CURRENT_MANIFEST_VERSION = DASHBOARD_PANEL_MANIFEST.manifestVersion;
export { DASHBOARD_PANEL_MANIFEST, CURRENT_MANIFEST_VERSION };
